import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { schemasApi } from '@/api/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { FileCode, Search, Plus, ChevronRight, Database, Eye } from 'lucide-react'

export function SchemasPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedSchema, setSelectedSchema] = useState<string | null>(null)

  const { data: schemas = [], isLoading } = useQuery({
    queryKey: ['schemas'],
    queryFn: () => schemasApi.list(),
  })

  const { data: schemaDetail } = useQuery({
    queryKey: ['schemas', selectedSchema],
    queryFn: () => schemasApi.get(selectedSchema!),
    enabled: !!selectedSchema,
  })

  const filteredSchemas = schemas.filter(
    (s) =>
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.schema_id.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold">Schema Registry</h2>
          <p className="text-muted-foreground">
            MetadataIQ schema definitions for Form Factory and data validation
          </p>
        </div>
        <Button variant="glow">
          <Plus className="h-4 w-4 mr-2" />
          New Schema
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Schema list */}
        <div className="lg:col-span-1 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search schemas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="space-y-2">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              filteredSchemas.map((schema) => (
                <button
                  key={schema.schema_id}
                  onClick={() => setSelectedSchema(schema.schema_id)}
                  className={`w-full text-left p-4 rounded-lg border transition-all ${
                    selectedSchema === schema.schema_id
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-muted-foreground/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                        <Database className="h-4 w-4 text-accent" />
                      </div>
                      <div>
                        <p className="font-medium">{schema.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          {schema.schema_id}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </button>
              ))
            )}

            {filteredSchemas.length === 0 && !isLoading && (
              <div className="text-center py-8 text-muted-foreground">
                <Database className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No schemas found</p>
              </div>
            )}
          </div>
        </div>

        {/* Schema detail */}
        <div className="lg:col-span-2">
          {selectedSchema && schemaDetail ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{schemaDetail.name}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Version {schemaDetail.version} • Module: {schemaDetail.module_id}
                    </p>
                  </div>
                  <Badge variant="secondary">{schemaDetail.fields.length} fields</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Field Definitions
                  </h4>
                  <div className="space-y-2">
                    {schemaDetail.fields.map((field) => (
                      <div
                        key={field.field_id}
                        className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                            <FileCode className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-medium">{field.display_name}</p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {field.field_id}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{field.field_type}</Badge>
                          {field.required && (
                            <Badge variant="destructive" className="text-xs">
                              Required
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-2 pt-4 border-t border-border">
                    <Button variant="outline">
                      <Eye className="h-4 w-4 mr-2" />
                      Preview Form
                    </Button>
                    <Button variant="ghost">Edit Schema</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="h-full flex items-center justify-center min-h-[400px]">
              <div className="text-center text-muted-foreground">
                <FileCode className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select a schema to view details</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
